import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import axios from 'axios'
import moment from 'moment'
import DataTable from 'react-data-table-component'

import Panel from '../ui/Panel'
import Btn from '../ui/Btn'
import Avatar from '../ui/Avatar'
import VideoModal from '../ui/VideoModal'
import { baseFileId, posterUrl } from '../ui/videoSource'
import {
  Confirm,
  Empty,
  IconOpen,
  IconPlay,
  LineSkeleton,
  Modal,
  Note,
  TableSkeleton,
  Thumb,
  fullName,
  useLatest,
  useTableStyles
} from './peopleUI'
import './people.css'

/*
 * A player's challenge entries, and the reactions each one collected.
 *
 * The API is untouched:
 *   GET  admin/users/challenges/:uuid
 *   GET  admin/users/challenges/ranking/:id/:postedBy
 *   GET  admin/users/challenges/delete/:id
 *   POST admin/users/challenges/addpoints
 *
 * The list used to mount a full video player in every row — dozens of HLS
 * players fighting for the same connection before the admin had clicked
 * anything. A row now shows the poster frame; the clip plays in the dialog,
 * where the reactions and the admin-points form already live.
 *
 * One real bug is fixed on the way: after adding points the screen reopened the
 * entry with `handleShow(content.id)` and no PostedBy, so the ranking request
 * went out as .../ranking/123/undefined and the reactions came back empty. The
 * second argument is now passed. The URL shape is exactly the same.
 *
 * VIDEO — nothing here builds a stream url any more.
 *
 * The dialog used to mount a <ReactPlayer> on a single hand-picked url. That
 * url was whatever `{id}.smil` happened to be, which 404s for a good part of
 * the library, and the player was locked to a 16/9 box, which letterboxes the
 * phone-shot portrait clips that make up most of what players submit.
 *
 * The clip now goes to the shared VideoModal and every id goes through
 * videoSource. That is what strips the extension the API sometimes attaches
 * ("OTH_123.mov" -> "OTH_123"), walks 1080_{id}.mp4 -> {id}.smil -> 720_{id}.mp4
 * until one answers, and reads the clip's real dimensions on loadedmetadata so
 * portrait and landscape both fill the frame.
 *
 * The player is handed the player's WHOLE set of clips plus a starting index,
 * so a reviewer works through a user's entries with the arrow keys instead of
 * closing and reopening the dialog for each one. Walking to another clip moves
 * the entry underneath it too, so the points form and the reaction list always
 * belong to the clip on screen.
 */

/*
 * The clip surface inside the entry dialog: the poster frame, and a press that
 * hands the clip to the player. Its twin lives in UserPosts.js — the natural
 * home is peopleUI, which this task does not own.
 */
function PosterButton({ fileId, label, onClick }) {
  const [broken, setBroken] = useState(false)
  const src = posterUrl(fileId)

  return (
    <button type="button" className="pp-poster" onClick={onClick} title={label} aria-label={label}>
      {src && !broken ? (
        <img src={src} alt="" loading="lazy" decoding="async" onError={() => setBroken(true)} />
      ) : (
        <span className="pp-poster-none">Video</span>
      )}
      <span className="pp-poster-play" aria-hidden="true"><IconPlay /></span>
    </button>
  )
}

export default function UserChallenges(props) {
  const [isLoading, setIsLoading] = useState(true)
  const [data, setData] = useState([])
  const [show, setShow] = useState(false)
  const [content, setContent] = useState({})
  const [reactions, setReactions] = useState([])

  const [failed, setFailed] = useState(false)
  const [reactionsLoading, setReactionsLoading] = useState(false)
  const [note, setNote] = useState(null)
  const [askDelete, setAskDelete] = useState(false)
  const [busy, setBusy] = useState(false)
  const [savingPoints, setSavingPoints] = useState(false)

  /* -1 = no clip open. Any other value indexes `clips` below, not `data`. */
  const [clipIndex, setClipIndex] = useState(-1)

  const points = useRef()
  const tableStyles = useTableStyles(false)

  /* The column array is memoised once, so handleShow() below closes over the
     first render's `data` — an empty array. It looks the row up through this
     ref, which always holds the rows currently on screen. */
  const dataRef = useLatest(data)

  /* The API hands the same id out under two names and in two shapes — VID is
     usually the clean "OTH_123", VideoID the same thing with the recorder's
     extension still on it. Whichever one a row carries, videoSource is what
     turns it into a poster; nothing here concatenates a path. */
  const fileIdOf = (row) => baseFileId((row && (row.VID || row.VideoID)) || '')

  const thumbFor = (row) => (
    row.Photo && row.Photo.length > 0
      ? process.env.REACT_APP_API_SERVER + 'postfiles/' + row.ContentUUID + '/123'
      : posterUrl(fileIdOf(row))
  )

  const columns = useMemo(() => [
    {
      name: '',
      cell: (row) => (
        <Thumb
          src={thumbFor(row)}
          label="Open this entry"
          onClick={() => handleShow(row.id, row.PostedBy)}
        />
      ),
      width: '104px'
    },
    {
      name: 'Description',
      selector: (row) => row.Descr || '',
      cell: (row) => (
        <button
          type="button"
          className="pp-name pp-wrap"
          title="Open this entry"
          onClick={() => handleShow(row.id, row.PostedBy)}
        >
          {row.Descr || 'Untitled entry'}
        </button>
      ),
      sortable: true,
      minWidth: '240px',
      grow: 3,
      wrap: true
    },
    {
      name: 'Ranking',
      selector: (row) => Number(row.v) || 0,
      cell: (row) => <span className="pp-num">{row.v == null ? '—' : row.v}</span>,
      sortable: true,
      width: '110px',
      right: true
    },
    {
      name: 'Date created',
      selector: (row) => row.DateCreated || '',
      cell: (row) => (
        <span className="pp-mono-dim">
          {row.DateCreated ? moment(row.DateCreated).format('DD/MM/YYYY') : '—'}
        </span>
      ),
      sortable: true,
      width: '134px'
    },
    {
      name: '',
      cell: (row) => (
        <button
          type="button"
          className="pp-rowbtn"
          title="Open this entry"
          aria-label="Open this entry"
          onClick={() => handleShow(row.id, row.PostedBy)}
        >
          <i><IconOpen /></i>
        </button>
      ),
      width: '60px',
      center: true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [])

  const reactionColumns = useMemo(() => [
    {
      name: 'Person who reacted',
      selector: (row) => fullName(row.FirstName, row.LastName),
      cell: (row) => (
        <div className="pp-face">
          <Avatar
            uuid={row.ReactUserID != '0' ? row.ReactUserUUID : undefined}
            name={fullName(row.FirstName, row.LastName)}
            size={28}
          />
          <span className="pp-cell-line">
            {row.ReactUserID == '0' ? 'Admin' : (fullName(row.FirstName, row.LastName) || '—')}
          </span>
        </div>
      ),
      sortable: true,
      minWidth: '200px',
      grow: 2
    },
    {
      name: 'Rank',
      selector: (row) => Number(row.RankType) || 0,
      cell: (row) => <span className="pp-num">{row.RankType}</span>,
      sortable: true,
      width: '90px',
      right: true
    }
  ], [])

  useEffect(() => {
    loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadData = () => {
    setIsLoading(true)
    setFailed(false)
    axios.get(process.env.REACT_APP_API_SERVER + 'admin/users/challenges/' + props.id, {
      headers: {
        'Authorization': 'Bearer ' + sessionStorage.getItem("apiToken")
      }
    })
      .then(res => {
        setData(res.data.results)
        setIsLoading(false)
      }).catch(err => {
        setIsLoading(false)
        setFailed(true)
        console.log(err)
      })
  }

  const handleShow = (id, PostedBy) => {
    setReactionsLoading(true)
    setNote(null)
    setAskDelete(false)

    axios.get(process.env.REACT_APP_API_SERVER + 'admin/users/challenges/ranking/' + id + '/' + PostedBy, {
      headers: {
        'Authorization': 'Bearer ' + sessionStorage.getItem("apiToken")
      }
    })
      .then(res => {
        setReactions(res.data.results)
        setReactionsLoading(false)
      }).catch(err => {
        setReactions([])
        setReactionsLoading(false)
        console.log(err)
      })

    setContent((dataRef.current || []).filter(item => item.id == id)[0] || {})
    setShow(true)
  }

  const handleClose = () => {
    setShow(false)
    setAskDelete(false)
  }

  const deleteContent = (id) => {
    setBusy(true)
    axios.get(process.env.REACT_APP_API_SERVER + 'admin/users/challenges/delete/' + id, {
      headers: {
        'Authorization': 'Bearer ' + sessionStorage.getItem("apiToken")
      }
    })
      .then(res => {
        setBusy(false)
        if (res.data.err == 0) {
          setShow(false)
          setAskDelete(false)
          loadData()
          setNote(null)
        } else {
          setNote({ tone: 'crit', text: 'An error has occured' })
        }
      }).catch(err => {
        setBusy(false)
        setNote({ tone: 'crit', text: 'An error has occured' })
        console.log(err)
      })
  }

  const addPoints = (e) => {
    e.preventDefault()
    setSavingPoints(true)

    const obj = {
      ChallengeID: content.id,
      ChUserID: content.PostedBy,
      ReactUserID: 0,
      RankValue: 1,
      RankType: points.current.value,
      PostType: 0,
    }

    axios.post(process.env.REACT_APP_API_SERVER + 'admin/users/challenges/addpoints', obj, {
      headers: {
        'Authorization': 'Bearer ' + sessionStorage.getItem("apiToken")
      }
    })
      .then(res => {
        setSavingPoints(false)
        if (res.data.err == 0) {
          if (points.current) points.current.value = ''
          loadData()
          handleShow(content.id, content.PostedBy)
        } else {
          setNote({ tone: 'crit', text: 'An error has occured' })
        }
      }).catch(err => {
        setSavingPoints(false)
        setNote({ tone: 'crit', text: 'An error has occured' })
        console.log(err)
      })
  }

  const rows = useMemo(() => (Array.isArray(data) ? data : []), [data])

  /* Every entry of this player that carries a clip, in list order. The player
     receives this whole set plus a starting index, so the reviewer walks the
     user's submissions inside it instead of reopening a dialog per clip. */
  const clips = useMemo(() => rows
    .map((row) => ({ row, fileId: fileIdOf(row) }))
    .filter((entry) => entry.fileId.length > 0)
    .map(({ row, fileId }) => ({
      id: row.id,
      postedBy: row.PostedBy,
      fileId,
      name: props.name || 'This player',
      uuid: props.uuid || props.id,
      score: row.v == null || row.v === '' ? undefined : row.v,
      scoreLabel: 'rank',
      subtitle: row.Descr || 'Challenge entry'
    })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, props.name, props.uuid, props.id])

  const clipOpen = clipIndex >= 0 && clipIndex < clips.length

  /* A delete or a refresh can shorten the list under an open player. */
  useEffect(() => {
    if (clipIndex >= clips.length) setClipIndex(-1)
  }, [clips.length, clipIndex])

  /* The player opens on top of this screen's own dialog and both of them listen
     for Escape on `document`, so one press would otherwise close the clip and
     the entry behind it together. While the player is up, Escape is the
     player's: this runs in the capture phase, stops the event dead, and closes
     the clip itself. */
  useEffect(() => {
    if (!clipOpen) return undefined

    const onKey = (ev) => {
      if (ev.key !== 'Escape' && ev.key !== 'Esc') return
      ev.stopImmediatePropagation()
      setClipIndex(-1)
    }

    document.addEventListener('keydown', onKey, true)
    return () => document.removeEventListener('keydown', onKey, true)
  }, [clipOpen])

  const closeClip = useCallback(() => setClipIndex(-1), [])

  const openClip = (id) => {
    const at = clips.findIndex((item) => item.id == id)
    if (at >= 0) setClipIndex(at)
  }

  /* Moving to another clip moves the entry under it as well, so the points form
     and the reactions never describe a different entry than the one playing.
     The request is the one this screen already makes — same url, same shape. */
  const walkClip = (next) => {
    setClipIndex(next)
    const target = clips[next]
    if (target) handleShow(target.id, target.postedBy)
  }

  const contentFileId = fileIdOf(content)
  const hasVideo = contentFileId.length > 0
  const hasPhoto = content.Photo && content.Photo.length > 0
  const clipPosition = clips.findIndex((item) => item.id == content.id)

  return (
    <Panel
      title="User Challenges"
      subtitle={
        isLoading
          ? 'Loading…'
          : rows.length + (rows.length === 1 ? ' entry' : ' entries')
      }
      actions={!isLoading && rows.length > 0 ? <Btn onClick={loadData}>Refresh</Btn> : null}
    >
      {note && !show ? (
        <Note tone={note.tone} onDismiss={() => setNote(null)}>{note.text}</Note>
      ) : null}

      {isLoading ? (
        <TableSkeleton rows={3} />
      ) : failed ? (
        <Empty
          title="Challenge entries could not be loaded"
          action={<Btn variant="accent" onClick={loadData}>Try again</Btn>}
        >
          The request did not come back. Nothing has been lost.
        </Empty>
      ) : rows.length === 0 ? (
        <Empty title="No challenge entries yet">
          Everything this player submits to a challenge shows up here, with the
          reactions it collected.
        </Empty>
      ) : (
        <div className="pp-table">
          <DataTable
            columns={columns}
            data={rows}
            customStyles={tableStyles}
            highlightOnHover
            persistTableHead
            fixedHeader
            fixedHeaderScrollHeight="52vh"
            pagination
            paginationPerPage={10}
            paginationRowsPerPageOptions={[10, 25, 50]}
            noDataComponent={<Empty title="No entries" />}
          />
        </div>
      )}

      <Modal
        open={show}
        size="lg"
        title={content.Descr || 'Challenge entry'}
        subtitle={
          content.DateCreated
            ? 'Created ' + moment(content.DateCreated).format('DD/MM/YYYY')
            : undefined
        }
        onClose={handleClose}
      >
        {note ? (
          <Note tone={note.tone} onDismiss={() => setNote(null)}>{note.text}</Note>
        ) : null}

        {hasVideo ? (
          <div className="pp-media pp-media-clip">
            <PosterButton
              fileId={contentFileId}
              label="Play this clip"
              onClick={() => openClip(content.id)}
            />
            {clips.length > 1 && clipPosition >= 0 ? (
              <div className="pp-poster-hint">
                Clip {clipPosition + 1} of {clips.length} · the player walks the rest
              </div>
            ) : null}
          </div>
        ) : null}

        {hasPhoto ? (
          <div className="pp-media">
            <img
              className="pp-photo"
              src={process.env.REACT_APP_API_SERVER + 'postfiles/' + content.ContentUUID + '/123'}
              alt="Challenge entry"
            />
          </div>
        ) : null}

        <fieldset className="pp-fieldset">
          <legend className="pp-legend">Admin points</legend>
          <form className="pp-form" onSubmit={addPoints}>
            <div className="pp-field">
              <label className="pp-label" data-req="1" htmlFor="pp-ch-points">Points</label>
              <input
                id="pp-ch-points"
                className="pp-input"
                type="number"
                min={-100}
                step={1}
                max={100}
                ref={points}
                required
                placeholder='Enter Admin Points'
              />
              <div className="pp-hint">
                Between -100 and 100. The entry keeps every reaction it already has —
                this is added on top as an admin reaction.
              </div>
            </div>
            <div className="pp-actions">
              <Btn variant="accent" type="submit" disabled={savingPoints}>
                {savingPoints ? 'Adding…' : 'Add points'}
              </Btn>
            </div>
          </form>
        </fieldset>

        <div className="pp-field">
          <div className="pp-label">
            Reactions{reactions && reactions.length ? ' · ' + reactions.length : ''}
          </div>
          {reactionsLoading ? (
            <LineSkeleton lines={3} />
          ) : !reactions || reactions.length === 0 ? (
            <Empty title="No reactions yet">
              Nobody has ranked this entry.
            </Empty>
          ) : (
            <div className="pp-table">
              <DataTable
                columns={reactionColumns}
                data={reactions}
                customStyles={tableStyles}
                highlightOnHover
                persistTableHead
                fixedHeader
                fixedHeaderScrollHeight="34vh"
                noDataComponent={<Empty title="No reactions" />}
              />
            </div>
          )}
        </div>

        {askDelete ? (
          <Confirm
            title="Delete this entry"
            confirmLabel="Delete entry"
            busy={busy}
            onCancel={() => setAskDelete(false)}
            onConfirm={() => deleteContent(content.id)}
          >
            This challenge entry and the reactions on it are removed permanently.
            The player keeps their account. This cannot be undone.
          </Confirm>
        ) : (
          <div className="pp-actions">
            <button type="button" className="btn-ui pp-crit" onClick={() => setAskDelete(true)}>
              Delete this content
            </button>
          </div>
        )}
      </Modal>

      {clipOpen ? (
        <VideoModal
          items={clips}
          index={clipIndex}
          title={props.name ? props.name + ' · challenge entries' : 'Challenge entries'}
          onIndex={walkClip}
          onClose={closeClip}
        />
      ) : null}
    </Panel>
  )
}
